import { Component, Input, OnInit } from '@angular/core';
import { SharedModule } from '@Shared/shared.module';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SystemEvent as SystemEventEnum } from 'app/enums/global/system-event.enum';
import { FeatureManagerService } from '@Services';
import * as _ from 'lodash';
import { pairwise } from 'rxjs/operators';
import { AppService } from 'app';
import { SystemEventAction, SystemEventActionType } from '../../../enums/global/system-event-actions.enum';
import { Account, AccountEvent, Client, SystemEvent } from '@Models';
import { AccountEventsService, AccountDataService, ConfirmationDialogService, NotificationService, NotificationSeverity, SignerService, TimerEventCaseCountResponse } from '../../../services';
import { EventActionAccordComponent } from './actions/event-action-accord/event-action-accord.component';
import { EventDisplayMessageActionComponent } from './actions/event-action-display-message/event-action-display-message.component';
import { EventEmailActionComponent } from './actions/event-action-email/event-action-email.component';
import { EventActionFaxComponent } from './actions/event-action-fax/event-action-fax.component';
import { EventActionGTLSubmissionComponent } from './actions/event-action-gtl-submission/event-action-gtl-submission.component';
import { EventIntegrationActionComponent } from './actions/event-action-integration/event-action-integration.component';
import { EventSMSActionComponent } from './actions/event-action-sms/event-action-sms.component';
import { EventActionWebhookComponent } from './actions/event-action-webhook/event-action-webhook.component';
import { FeatureToggle } from '@Enums';
import { ConfigDataDTO } from '@DTOs';
import { EventActionArcusTransmissionComponent } from './actions/event-action-arcus-transmission/event-action-arcus-transmission.component';
import { MapKeysDropdownComponent } from '@Components/map-keys-dropdown/map-keys-dropdown.component';
import { ObjectTagsComponent } from '@Components/object-tags/object-tags.component';
import { UWRequirementsDataService, UWWorkbenchService } from '@CaseSummary/underwriting/services';

interface NamedConditionGroup {
    param: any;
    options: any[];
    formControlName: string;
    conditionalOnParam?: string;
}

const REQUIREMENT_NOTE_ADDED_IPAS_KEY = 'requirementNoteAddedIpas';

@Component({
    selector: 'account-events',
    host: { 'class': 'd-flex flex-row flex-fill custom-scrollbar bg-white' },
    templateUrl: './events.component.html',
    styleUrls: ['./events.component.scss'],
    imports: [SharedModule, MapKeysDropdownComponent, ObjectTagsComponent]
})
export class EventsComponent implements OnInit {
    @Input() accountInfo: Account;
    @Input() client: Client;
    @Input() rules;
    @Input() mapKeys;
    @Input() nonListMapKeys;
    @Input() dateFieldMapKeys;
    public eventForm: UntypedFormGroup;
    events: AccountEvent[];
    eventTypes: SystemEvent[];
    eventConditions: any;
    actionConditions: any;
    toggleConditions: any;
    mapkeyCondition = false;
    tagCondition = false;
    inputCondition = false;
    dateFieldCondition = false;
    mapkeyConditionParams: any;
    dateFieldConditionParams: any;
    tagConditionParams: any;
    inputConditionParams: any[] = [];
    selectedTags: any[];
    actions: any;
    filteredActions: any;
    systemEventActionFilters: any;
    selectedEvent: AccountEvent;
    selectedAction: any;
    configData: ConfigDataDTO;
    searchTerm = '';
    orderApiCategory: boolean;
    noteAddedEvent: boolean;
    requirementNoteAddedEvent: boolean;
    multiNamedEventConditions: NamedConditionGroup[] = [];
    eventConditionLabel: any;
    eventConditionHint: any;
    multipleEventConditions: boolean;
    multipleActionConditions: boolean = false;
    conditionIsRequired: boolean = true;
    actionConditionLabel: any;
    toggleConditionLabel: any;
    mapkeyConditionLabel: any;
    formSubmitted = false;
    emailDialogRef: MatDialogRef<EventEmailActionComponent> | undefined;
    displayMessageDialogRef: MatDialogRef<EventDisplayMessageActionComponent> | undefined;
    displayIntegrationDialogRef: MatDialogRef<EventIntegrationActionComponent> | undefined;
    displaySmsDialogRef: MatDialogRef<EventSMSActionComponent> | undefined;
    displayWebhookDialogRef: MatDialogRef<EventActionWebhookComponent> | undefined;
    displayGTLDialogRef: MatDialogRef<EventActionGTLSubmissionComponent> | undefined;
    displayArcusDialogRef: MatDialogRef<EventActionArcusTransmissionComponent> | undefined;
    displayAccordDialogRef: MatDialogRef<EventActionAccordComponent> | undefined;
    displayFaxDialogRef: MatDialogRef<EventActionFaxComponent> | undefined;
    
    conditionIdField: string = 'id';
    conditionNameField: string = 'name';

    private readonly actionIdsWithConditions = new Set<string>([
        SystemEventAction.StatusChange,
        SystemEventAction.BillingAdd,
        SystemEventAction.BillingRemove,
        SystemEventAction.EvaluateUwwRequirementRules,
        SystemEventAction.EvaluateUwwTaskRules
    ]);

    private readonly actionIdsWithoutEditableConfig = new Set<string>([
        SystemEventAction.MarkCaseDone,
        SystemEventAction.ProcessInterview,
        SystemEventAction.StatusChange,
        SystemEventAction.ProcessInterviewAccumulateErrors,
        SystemEventAction.EvaluateCaseManager,
        SystemEventAction.LongLivedIntegrationStatusUpdate,
        SystemEventAction.EvaluateUwwRequirementRules,
        SystemEventAction.BillingAdd,
        SystemEventAction.BillingRemove,
        SystemEventAction.EvaluateVirtualMapkeys,
        SystemEventAction.EvaluateUwwTaskRules
    ]);
    
    timerTriggerEnum = SystemEventEnum.TimerTrigger;

    constructor(
        public dialog: MatDialog,
        private appService: AppService,
        private accountEventsService: AccountEventsService,
        private accountDataService: AccountDataService,
        private confirmationService: ConfirmationDialogService,
        private notificationService: NotificationService,
        private signerService: SignerService,
        private _fb: UntypedFormBuilder,
        private featureManagerService: FeatureManagerService,        
        private readonly uwRequirementsDataService: UWRequirementsDataService,
        private readonly uwWorkbenchService: UWWorkbenchService
    ) {
    }

    ngOnInit() {
        this.accountDataService.getSecretKeys().subscribe();
        this.eventForm = this._fb.group({
            id: [''],
            isActive: [''],
            systemEventActionId: ['', Validators.required],
            name: ['', Validators.required],
            systemEventId: ['', Validators.required],
            ruleId: ['']
        });

        this.appService.getAllConfigData().then(configDataDTO => {
            this.configData = configDataDTO;
            this.eventTypes = this.configData.systemEvents.map(e => ({ ...e }));

            this.actions = this.filterSystemEventActions(this.configData.systemEventActions);
            this.systemEventActionFilters = this.configData.systemEventActionFilters;

            this.appService.getAllDynamicConfigData(this.accountInfo.id).then(() => {
                this.markUnavailableEventTypes();
            });

            this.uwRequirementsDataService.loadRequirements(this.client.code, this.accountInfo.id).subscribe({
                next: (requirements) => {
                    this.configData.requirements = requirements.filter( x=> x.ruleId !== null);
                }
            });

            this.uwWorkbenchService.getTasks(this.client.code, this.accountInfo).subscribe({
                next: (tasks) => {
                    this.configData.tasks = tasks.filter( x=> x.ruleId !== null);
                }
            });

        });

        this.orderApiCategory = this.featureManagerService.getByName('Case.Event.OrderApi.Category').enabled;
        this.noteAddedEvent = this.featureManagerService.getByName('Global.Events.NoteAdded').enabled;
        this.requirementNoteAddedEvent = this.featureManagerService.getByName('Global.Events.RequirementNoteAdded').enabled;

        this.eventForm.get('systemEventId').valueChanges.pipe(pairwise())
            .subscribe(([prev, next]: [any, any]) => {
                if (prev !== next) {
                    if (next) {
                        this.selectedEvent.systemEventId = next;
                        if (!this.selectedEvent.id || this.selectedEvent.id === null) {
                            this.selectedEvent.name = this.eventForm.controls['name'].value;
                        }

                        if (prev) {
                            this.selectedEvent.conditions = null;
                            this.selectedEvent.conditionId = null;
                            if (this.eventForm.get('conditionId')) {
                                this.eventForm.get('conditionId').reset();
                            }
                        }

                    }

                    this.inputCondition = false;
                    this.inputConditionParams = [];
                    this.eventConditions = null;
                    this.multipleEventConditions = false;
                    this.multiNamedEventConditions = [];
                    this.mapkeyCondition = false;
                    this.dateFieldCondition = false;
                    this.tagCondition = false;
                    this.selectedTags = [];

                    this.getEventParams(next);
                    this.filterEventActions();
                }

                if (next === this.timerTriggerEnum) {
                    this.eventForm.controls["ruleId"].addValidators(Validators.required);
                    this.eventForm.controls["ruleId"].updateValueAndValidity();
                }

            });
        this.eventForm.get('systemEventActionId').valueChanges.pipe(pairwise())
            .subscribe(([prev, next]: [any, any]) => {
                if (prev !== next) {
                    if (next) {
                        this.selectedEvent.systemEventActionId = next;

                        if (!this.selectedEvent.id || this.selectedEvent.id === null) {
                            this.selectedEvent.name = this.eventForm.controls['name'].value;
                        }
                    }
                    this.selectedAction = next;
                    this.getActionParams(next);
                }
            });

        this.getAccountEvents();
    }

    get SystemEventEnum() { return SystemEventEnum; }

    get selectedSystemEventActionId(): string | null {
        return this.eventForm?.get('systemEventActionId')?.value ?? null;
    }

    get hasEventConditions(): boolean {
        return !!this.eventConditions?.length;
    }

    get showSingleEventCondition(): boolean {
        return this.hasEventConditions && !this.multipleEventConditions;
    }

    get showMultiEventCondition(): boolean {
        return this.hasEventConditions && this.multipleEventConditions;
    }

    get canEditSelectedAction(): boolean {
        const actionId = this.selectedSystemEventActionId;
        return !!actionId && !this.actionIdsWithoutEditableConfig.has(actionId);
    }

    get showActionConditionSelector(): boolean {
        const actionId = this.selectedSystemEventActionId;
        return !!this.actionConditions?.length
            && !!actionId
            && this.actionIdsWithConditions.has(actionId);
    }

    get showNoEventsState(): boolean {
        return !!this.events && this.events.length === 0;
    }

    get filteredEvents(): AccountEvent[] {
        if (!this.events) return [];
        const term = this.searchTerm?.trim().toLowerCase();
        if (!term) return this.events;
        return this.events.filter(event => {
            const nameMatch = event.name?.toLowerCase().includes(term);
            const eventType = this.eventTypes?.find(e => e.id === event.systemEventId);
            const eventTypeMatch = eventType?.name?.toLowerCase().includes(term);
            const action = this.actions?.find(a => a.id === event.systemEventActionId);
            const actionMatch = action?.name?.toLowerCase().includes(term);
            return nameMatch || eventTypeMatch || actionMatch;
        });
    }

    getAccountEvents() {
        this.accountEventsService.getAccountEvents(this.accountInfo.id).subscribe(accountEvents => {
            if (!this.noteAddedEvent) {
                accountEvents = accountEvents.filter(obj => obj.name !== 'Note Added');
            }
            if (!this.requirementNoteAddedEvent) {
                accountEvents = accountEvents.filter(obj => obj.name !== 'Requirement Note Added');
            }

            this.events = _.sortBy(accountEvents, [event => event.name.toLowerCase()]);
        });
    }

    getEventParams(option: any) {
        if (!this.toggleConditions
            || this.toggleConditions.name !== "regenerateDocuments"
            || !this.selectedEvent
            || this.selectedEvent.systemEventActionId !== SystemEventAction.MarkCaseDone) {
            this.toggleConditions = null;
        }
        if (!option) {
            return false;
        }
        const eventType = _.find(this.eventTypes, { 'id': option });
        if (eventType.params) {
            const params = JSON.parse(eventType.params);
            // Rebuilt on every call because an event can expose more than one input param and this method
            // runs again whenever the selected event changes.
            this.inputCondition = false;
            this.inputConditionParams = [];
            params.forEach(param => {
                if (param.paramType === 'lookup' && param.lookupEnum) {
                    this.eventConditions = this.configData[param.lookupEnum];

                    this.setupConditions(param);
                } else if (param.paramType === 'configLookup' && param.lookupEnum === 'accountSigner') {
                    this.signerService.getAccountSigners(this.accountInfo.id).subscribe(data => {
                        data.forEach(x => {
                            x.name = x.label;
                        });

                        this.eventConditions = data;
                        this.setupConditions(param);
                    });
                } else if (param.paramType === 'rawJson') {
                    this.eventConditions = params;

                    let paramJson = _.find(params, { 'code': this.selectedEvent.conditions });
                    if (param.checkFeatureToggle) {
                        if (param.checkFeatureToggle == "OrderApiCategory") {
                            if (!this.orderApiCategory) {
                                this.selectedEvent.conditions = null;
                                this.eventConditions = null;
                                return;
                            }
                        }
                    }

                    if (!paramJson) {
                        paramJson = param;
                    } else {
                        paramJson.paramType = param.paramType;
                        paramJson.label = param.label;
                    }
                    this.setupConditions(paramJson);
                } else if (param.paramType === 'toggle') {
                    this.toggleConditions = param;
                    this.setupToggleConditions(param);
                } else if (param.paramType === 'multi-lookup') {
                    const options = this.excludeParamValues(param, this.configData[param.lookupEnum]);
                    if (param.name) {
                        const namedOptions = this.excludeParamValues(param, this.appService.getConfigFromStaticAndDynamicConfigData(param.lookupEnum));
                        this.multiNamedEventConditions.push({
                            param,
                            options: namedOptions,
                            formControlName: param.name,
                            conditionalOnParam: param.conditionalOnParam
                        });
                        this.setupNamedMultiLookupConditions(param);
                    } else {
                        this.eventConditions = options;
                        this.setupConditions(param, param.required);
                    }
                }else if (param.paramType === 'configLookup' && param.lookupEnum === 'accountDocument') {
                    this.appService.getData(`Documents/${this.accountInfo.id}`).subscribe(result => {
                        this.eventConditions = result.data;
                        this.setupConditions(param, false);
                    });
                } else if (param.paramType === 'mapKeys') {
                    this.mapkeyCondition = true;
                    this.mapkeyConditionParams = param;
                    this.setupMapkeyConditions(param, param.required);
                } else if (param.paramType === 'dateField') {
                    this.dateFieldCondition = true;
                    this.dateFieldConditionParams = param;
                    this.setupDateFieldCondition(param);
                } else if (param.paramType === 'tags') {
                    this.tagCondition = true;
                    this.tagConditionParams = param;
                    this.setupTagConditions(param, param.required);

                    if (this.eventForm.get('conditionId')) {
                        this.eventForm.removeControl('conditionId');
                    }
                } else if (param.paramType === 'input') {
                    this.inputCondition = true;
                    this.inputConditionParams.push(param);
                    this.setupInputConditions(param, param.required);
                }
            });

            this.applyCrossFieldValidators();
        } else {
            this.eventConditions = null;
            this.applyCrossFieldValidators();
            if (this.eventForm.get('conditionId')) {
                this.eventForm.removeControl('conditionId');
            }
        }
    }

    /**
     * Cross field rules live on the form group rather than the individual controls so they re-run whenever any
     * control changes, without needing a subscription per field. Driven by param metadata, so an event declares the
     * relationship instead of this component hard coding field names.
     */
    private applyCrossFieldValidators() {
        const rules = this.inputConditionParams.filter(param => param.lessThanParam);

        this.eventForm.setValidators(rules.length ? this.buildLessThanValidator(rules) : null);
        this.eventForm.updateValueAndValidity({ emitEvent: false });
    }

    /**
     * Guards against an inverted window, for example a minimum search days back at or beyond the maximum, which would
     * silently match zero cases every hour.
     */
    private buildLessThanValidator(rules: any[]) {
        return (group: UntypedFormGroup) => {
            const errors = {};

            rules.forEach(rule => {
                const raw = group.get(rule.name)?.value;
                const otherRaw = group.get(rule.lessThanParam)?.value;

                if (raw === '' || raw === null || raw === undefined || otherRaw === '' || otherRaw === null || otherRaw === undefined) {
                    return;
                }

                const value = Number(raw);
                const other = Number(otherRaw);

                if (Number.isNaN(value) || Number.isNaN(other)) {
                    return;
                }

                if (value >= other) {
                    errors[this.lessThanErrorKey(rule.name)] = true;
                }
            });

            return Object.keys(errors).length ? errors : null;
        };
    }

    lessThanErrorKey(paramName: string): string {
        return `${paramName}NotLessThan`;
    }

    getActionParams(option: any) {
        if (!option) {
            return false;
        }

        if (option !== SystemEventAction.MarkCaseDone && this.toggleConditions && this.toggleConditions.name === "regenerateDocuments") {
            this.toggleConditions = null;
        }

        const actionType = _.find(this.actions, { 'id': option });
        if (actionType.params) {
            const params = JSON.parse(actionType.params);
            if (params.length > 0) {
                const param = params[0];
                if (param.paramType === 'lookup' && param.lookupEnum) {
                    this.actionConditions = this.configData[param.lookupEnum];

                    this.setupActionConditions(param);
                }
                else if (param.paramType === 'multi-lookup' && param.lookupEnum) {
                    this.actionConditions = this.appService.getConfigFromStaticAndDynamicConfigData(param.lookupEnum);

                    if (param.idField && param.idField !== '') {
                        this.conditionIdField = param.idField;
                    }

                    if (param.nameField && param.nameField !== '') {
                        this.conditionNameField = param.nameField;
                    }

                    this.multipleActionConditions = true;
                    this.conditionIsRequired = typeof param.isRequired === 'boolean' ? param.isRequired : true;

                    this.actionConditions.sort((a, b) => a[this.conditionNameField].localeCompare(b[this.conditionNameField]));
                    this.setupActionConditions(param);                    

                } else if (param.paramType === 'configLookup' && param.lookupEnum === 'accountSigner') {
                    this.signerService.getAccountSigners(this.accountInfo.id).subscribe(data => {
                        data.forEach(x => {
                            x.name = x.label;
                        });

                        this.actionConditions = data;
                        this.setupActionConditions(param);
                    });
                } else if (param.paramType === 'rawJson') {
                    this.actionConditions = params;
                    this.setupActionConditions(param);
                } else if (param.paramType === 'toggle') {
                    this.toggleConditions = param;
                    this.setupToggleConditions(param);
                }
            }
        } else {
            this.actionConditions = null;
            if (this.eventForm.get('actionConditionId')) {
                this.eventForm.removeControl('actionConditionId');
            }
        }
    }

    filterSystemEventActions(systemEventActions: any) {
        if (!systemEventActions)
            return [];

        const skipFilteringClientIds = ['7ff6fc11-3cd8-4f81-9498-799b7983a87d', 'eae69d02-3b32-4a7b-b18d-0a137efb9306'];
        const filteredEvents = systemEventActions.filter(action =>
            skipFilteringClientIds.includes(this.accountInfo.clientId) ||
            (
                !Object.prototype.hasOwnProperty.call(action, 'clientId') ||
                action.clientId === this.accountInfo.clientId
            )
        );

        return filteredEvents;
    }

    private markUnavailableEventTypes() {
        const requirementNoteAddedIpas = this.appService.getDynamicConfigData(REQUIREMENT_NOTE_ADDED_IPAS_KEY);
        const hasIpasConfigured = requirementNoteAddedIpas && requirementNoteAddedIpas.length > 0;

        if (!hasIpasConfigured) {
            const reqNoteAddedEvent = this.eventTypes?.find(e => e.id === SystemEventEnum.RequirementNoteAdded);
            if (reqNoteAddedEvent) {
                reqNoteAddedEvent['disabled'] = true;
                reqNoteAddedEvent['disabledTooltip'] = 'LexisNexis Health Intelligence or APPS integration is not configured as a post action for this account';
            }
        }
    }

    private setupConditions(param: any, required = true) {
        this.eventConditionLabel = param.label;
        this.eventConditionHint = param.hint;

        if (!this.eventForm.get('conditionId') && required) {
            this.eventForm.addControl('conditionId', new UntypedFormControl('', Validators.required));
        }else  if (!this.eventForm.get('conditionId') && !required) {
            this.eventForm.addControl('conditionId', new UntypedFormControl(''));
        }

        // Set the control property based on paramtype
        if (param.paramType === 'multi-lookup') {
            this.multipleEventConditions = true;
        } else {
            this.multipleEventConditions = false;
        }

        if (this.selectedEvent != null && this.selectedEvent.conditions) {
            const conditions = JSON.parse(this.selectedEvent.conditions);
            if (param.paramType === 'multi-lookup') {

                this.selectedEvent.conditionId = conditions['caseStatusV2'].split(',');
                this.eventForm.patchValue(this.selectedEvent);

            } else if (param.paramType != 'rawJson') {

                this.selectedEvent.conditionId = conditions[param.lookupEnum];
                this.eventForm.patchValue(this.selectedEvent);

            } else {

                this.selectedEvent.conditionId = param.id;
                this.eventForm.patchValue(this.selectedEvent);

            }
        }
    }

    private setupActionConditions(param: any) {
        this.actionConditionLabel = param.label;
        const isRequired = typeof param.isRequired === 'boolean' ? param.isRequired : true;

        if (!this.eventForm.get('actionConditionId')) {
            this.eventForm.addControl('actionConditionId', new UntypedFormControl('', isRequired ? Validators.required : null));
        }
        if (this.selectedEvent != null && this.actionConditions && this.selectedEvent.actionConditionId) {
            this.eventForm.patchValue(this.selectedEvent);
        }
    }

    private setupToggleConditions(param: any) {
        this.toggleConditionLabel = param.label;

        if (!this.eventForm.get(param.name)) {
            this.eventForm.addControl(param.name, new UntypedFormControl(''));
            this.eventForm.controls[param.name].patchValue(false);
        }

        if (this.selectedEvent != null && this.selectedEvent.conditions) {
            const conditions = JSON.parse(this.selectedEvent.conditions);
            if (conditions[param.name]) {
                this.eventForm.controls[param.name].patchValue(conditions[param.name]);
            }
        }
    }

    private setupMapkeyConditions(param: any, isRequired = false) {
        this.mapkeyConditionLabel = param.label;

        if (!this.eventForm.get(param.name) && isRequired) {
            this.eventForm.addControl(param.name, new UntypedFormControl('', Validators.required));
        }else  if (!this.eventForm.get(param.name) && !isRequired) {
            this.eventForm.addControl(param.name, new UntypedFormControl(''));
        }

        if (this.selectedEvent != null && this.selectedEvent.conditions) {
            const conditions = JSON.parse(this.selectedEvent.conditions);
            if (conditions[param.name]) {
                this.eventForm.controls[param.name].patchValue(conditions[param.name]);
            }
        }
    }

    /**
     * Always optional: an empty value means the search window is measured against Case Creation Date, so there is
     * nothing to require here.
     */
    private setupDateFieldCondition(param: any) {
        if (!this.eventForm.get(param.name)) {
            this.eventForm.addControl(param.name, new UntypedFormControl(''));
        }

        const conditions = this.selectedEvent?.conditions ? JSON.parse(this.selectedEvent.conditions) : {};
        this.eventForm.controls[param.name].patchValue(conditions[param.name] ?? '');
    }

    private setupTagConditions(param: any, isRequired = false) {
        if (!this.eventForm.get(param.name) && isRequired) {
            this.eventForm.addControl(param.name, new UntypedFormControl('', Validators.required));
        }else  if (!this.eventForm.get(param.name) && !isRequired) {
            this.eventForm.addControl(param.name, new UntypedFormControl(''));
        }

        if (this.selectedEvent != null && this.selectedEvent.conditions) {
            const conditions = JSON.parse(this.selectedEvent.conditions);
            if (conditions[param.name]) {
                this.eventForm.controls[param.name].patchValue(conditions[param.name]);

                this.selectedTags = conditions[param.name].split(',');
            }
        }
    }

    private setupInputConditions(param: any, isRequired = false) {
        if (!this.eventForm.get(param.name)) {
            this.eventForm.addControl(param.name, new UntypedFormControl('', this.buildInputValidators(param, isRequired)));
        }

        if (this.selectedEvent != null && this.selectedEvent.conditions) {
            const conditions = JSON.parse(this.selectedEvent.conditions);
            if (conditions[param.name]) {
                this.eventForm.controls[param.name].patchValue(conditions[param.name]);
            }
        }
    }

    /**
     * Metadata driven validators so a param can cap what a configurer is allowed to enter, for example the 365 day
     * ceiling on a timer event search window.
     */
    private buildInputValidators(param: any, isRequired: boolean) {
        const validators = [];

        if (isRequired) {
            validators.push(Validators.required);
        }
        if (typeof param.min === 'number') {
            validators.push(Validators.min(param.min));
        }
        if (typeof param.max === 'number') {
            validators.push(Validators.max(param.max));
        }

        return validators;
    }

    /**
     * Lets a param metadata blob hide options that are never valid for it, for example terminal case statuses on a
     * timer event.
     */
    private excludeParamValues(param: any, options: any[]) {
        if (!options || !param.excludeValues || param.excludeValues.length === 0) {
            return options;
        }

        const excluded = new Set(param.excludeValues.map(value => `${value}`.toLowerCase()));

        return options.filter(option => !excluded.has(`${option.id}`.toLowerCase()));
    }
    private setupNamedMultiLookupConditions(param: any) {
        if (!this.eventForm.get(param.name)) {
            this.eventForm.addControl(
                param.name,
                new UntypedFormControl('', param.required ? Validators.required : null)
            );
        }
        if (this.selectedEvent?.conditions) {
            const conditions = JSON.parse(this.selectedEvent.conditions);
            if (conditions[param.name]) {
                const saved = conditions[param.name];
                this.eventForm.controls[param.name].patchValue(param.multiple === false ? saved : saved.split(','));
            }
        }
    }

    // For a condition group whose options depend on another group's current selection (e.g. Note Tags
    // depends on which Requirement(s) are selected), filter its options down to the union of
    // integrations represented by the selected item(s) in the controlling group.
    getConditionGroupOptions(conditionGroup: NamedConditionGroup): any[] {
        if (!conditionGroup.conditionalOnParam) {
            return conditionGroup.options;
        }

        const controllingValue = this.eventForm.get(conditionGroup.conditionalOnParam)?.value;
        const selectedValues: any[] = Array.isArray(controllingValue) ? controllingValue : (controllingValue ? [controllingValue] : []);
        if (!selectedValues.length) {
            return [];
        }

        const controllingGroup = this.multiNamedEventConditions.find(g => g.formControlName === conditionGroup.conditionalOnParam);
        const selectedIntegrationIds = new Set(
            controllingGroup?.options
                .filter(o => selectedValues.includes(o.id))
                .map(o => o.integrationId)
                .filter(id => !!id)
        );
        if (!selectedIntegrationIds.size) {
            return [];
        }

        return conditionGroup.options.filter(o => selectedIntegrationIds.has(o.integrationId));
    }

    eventActiveToggled(event) {
        event.isActive = !event.isActive;
        this.saveEditAccount(event);
    }

    selectEvent(event: AccountEvent) {
        if (this.selectedEvent === event) {
            this.selectedEvent = null;
            this.eventConditions = null;
            this.multiNamedEventConditions = [];
        } else {
            this.eventConditions = null;
            this.multiNamedEventConditions = [];
            this.formSubmitted = false;
            this.eventForm.reset();
            this.selectedEvent = event;
            if (this.selectedEvent.systemEventActionId === SystemEventAction.StatusChange) {
                this.selectedEvent.actionConditionId = JSON.parse(this.selectedEvent.configuration).statusId;
            }

            if (this.selectedEvent.systemEventActionId === SystemEventAction.StatusChange) {
                this.selectedEvent.actionConditionId = JSON.parse(this.selectedEvent.configuration).statusId;
            }

            if ((this.selectedEvent.systemEventActionId === SystemEventAction.BillingAdd || this.selectedEvent.systemEventActionId === SystemEventAction.BillingRemove) && this.selectedEvent.configuration) {
                this.selectedEvent.actionConditionId = (JSON.parse(this.selectedEvent.configuration).billingEventIds).split(',');
            }

            if (this.selectedEvent.systemEventActionId === SystemEventAction.EvaluateUwwRequirementRules && this.selectedEvent.configuration) {
                this.selectedEvent.actionConditionId = (JSON.parse(this.selectedEvent?.configuration)?.requirementIds)?.split(',');
            }

            if (this.selectedEvent.systemEventActionId === SystemEventAction.EvaluateUwwTaskRules && this.selectedEvent.configuration) {
                this.selectedEvent.actionConditionId = (JSON.parse(this.selectedEvent?.configuration)?.taskIds)?.split(',');
            }

            if (this.selectedEvent.systemEventActionId === SystemEventEnum.AddAttachment) {
                this.selectedTags = [];
                const configuration = JSON.parse(this.selectedEvent.configuration);
                if (configuration.attachmentTags){
                    this.selectedTags = configuration.split(',');
                }
            }
            this.selectedTags = [];


            if (this.selectedEvent.systemEventActionId === SystemEventAction.MarkCaseDone) {
                if (!this.selectedEvent.configuration) {
                    this.selectedEvent.regenerateDocuments = false;

                } else {
                    this.selectedEvent.regenerateDocuments = JSON.parse(this.selectedEvent.configuration).regenerateDocuments;
                }
            }
            this.eventForm.patchValue(this.selectedEvent);
        }
    }

    editAction(event) {
        const systemEventActionId = this.eventForm.controls['systemEventActionId'].value;

        if (systemEventActionId === SystemEventAction.Email) {
            this.emailDialogRef = this.dialog.open(EventEmailActionComponent, {
                width: '50%',
                data: {
                    accountId: this.accountInfo.id,
                    accountEvent: event,
                    clientId: this.accountInfo.clientId
                }
            });
        } else if (systemEventActionId === SystemEventAction.DisplayMessage) {
            this.displayMessageDialogRef = this.dialog.open(EventDisplayMessageActionComponent, {
                width: '50%',
                height: '450px',
                data: {
                    event: event,
                    mapKeys: this.mapKeys
                }
            });
        } else if (systemEventActionId === SystemEventAction.Integration) {
            this.displayIntegrationDialogRef = this.dialog.open(EventIntegrationActionComponent, {
                width: '50%',
                data: {
                    event: event,
                    mapKeys: this.mapKeys,
                    accountInfo: this.accountInfo
                },

            });
        } else if (systemEventActionId === SystemEventAction.SendSMS) {
            this.displaySmsDialogRef = this.dialog.open(EventSMSActionComponent, {
                width: '50%',
                data: {
                    event: event,
                    mapKeys: this.mapKeys,
                    accountInfo: this.accountInfo
                },

            });
        } else if (systemEventActionId === SystemEventAction.Webhook ) {
            this.displayWebhookDialogRef = this.dialog.open(EventActionWebhookComponent, {
                width: '50%',
                data: {
                    event: event,
                    accountInfo: this.accountInfo,
                    mapkeys: this.mapKeys
                },

            });
        } else if (systemEventActionId === SystemEventAction.ArcusSubmission ) {
            this.displayArcusDialogRef = this.dialog.open(EventActionArcusTransmissionComponent, {
                width: '50%',
                data: {
                    event: event,
                    accountInfo: this.accountInfo,
                    mapkeys: this.mapKeys
                },

            });
        } else if (systemEventActionId === SystemEventAction.GTLSubmission) {
            this.displayGTLDialogRef = this.dialog.open(EventActionGTLSubmissionComponent, {
                width: '50%',
                data: {
                    event: event,
                    accountInfo: this.accountInfo,
                    mapKeys: this.mapKeys,
                    nonListMapKeys: this.nonListMapKeys,
                },

            });
        } else if (systemEventActionId === SystemEventAction.AcordEvent) {
            this.displayAccordDialogRef = this.dialog.open(EventActionAccordComponent, {
                width: '50%',
                data: {
                    event: event,
                    accountInfo: this.accountInfo,
                    mapKeys: this.mapKeys
                },

            });
        } else if (systemEventActionId === SystemEventAction.Fax) {
            this.displayFaxDialogRef = this.dialog.open(EventActionFaxComponent, {
                width: '50%',
                data: {
                    event: event,
                    accountInfo: this.accountInfo
                },

            });
        }
    }

    cancelEvent(accountEvent: AccountEvent) {
        if (accountEvent.id == null) {
            this.events = _.reject(this.events, function (o) { return o === accountEvent; });
        }
        this.selectedEvent = null;
    }

    addAccountEvent() {
        this.formSubmitted = false;
        const accountEvent: AccountEvent = {
            accountId: '',
            isActive: true,
            name: '',
            conditions: '',
            systemEventId: '',
            systemEventActionId: '',
            ruleId: '',
            conditionId: '',
        };
        this.eventConditions = null;
        this.selectEvent(accountEvent);
        this.selectedEvent.regenerateDocuments = false;
        this.inputCondition = false;
        this.inputConditionParams = [];
        this.mapkeyCondition = false;
        this.dateFieldCondition = false;
        this.tagCondition = false;
        this.multiNamedEventConditions = [];
        this.selectedTags = [];
        this.applyCrossFieldValidators();
        if (this.events) {
            this.events.unshift(accountEvent);
        } else {
            this.events = [];
            this.events.unshift(accountEvent);
        }
    }

    saveAccountEvent() {
        if (this.eventForm.controls.systemEventId.value === SystemEventEnum.DeliverRemoteSigningMessage
            && (this.eventConditions === null || this.eventConditions.length <= 0)) {
            this.notificationService.showNotification({
                severity: NotificationSeverity.Error,
                message: 'Set up a Document Account Signer on the Settings tab before trying to add a Remote Signing Message',
                log: false
            });

            return false;
        }

        this.formSubmitted = true;
        if (!this.eventForm.valid) {
            this.eventForm.markAsTouched();
            this.eventForm.controls.name.markAsTouched();
            this.eventForm.controls.systemEventId.markAsTouched();
            if (this.eventForm.controls.conditionId) {
                this.eventForm.controls.conditionId.markAsTouched();
            }
            if (this.eventForm.controls.actionConditionId) {
                this.eventForm.controls.actionConditionId.markAsTouched();
            }
            if (this.eventForm.controls.systemEventActionId) {
                this.eventForm.controls.systemEventActionId.markAsTouched();
            }
            return false;
        }

        if (this.eventForm.controls.systemEventActionId.value !== SystemEventAction.Integration
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.MarkCaseDone
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.ProcessInterview
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.ProcessInterviewAccumulateErrors
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.Fax
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.StatusChange
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.EvaluateCaseManager
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.LongLivedIntegrationStatusUpdate
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.LongLivedEvidenceReceived
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.StandardIntegrationStatusChange
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.InitializeWorkbenchRequirements
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.EvaluateUwwRequirementRules
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.BillingAdd
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.BillingRemove
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.EvaluateVirtualMapkeys
            && this.eventForm.controls.systemEventActionId.value !== SystemEventAction.EvaluateUwwTaskRules) {
            if (!this.selectedEvent.accountMessage) {
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: 'Please add action properties...', log: false });
                return false;
            }
        }

        const actionConditionValue = this.eventForm.get('actionConditionId')?.value;
        const hasActionConditionValue = Array.isArray(actionConditionValue)
            ? actionConditionValue.some(value => value !== null && value !== undefined && `${value}`.trim() !== '')
            : actionConditionValue !== null && actionConditionValue !== undefined && `${actionConditionValue}`.trim() !== '';
        const actionRequiresConditionConfiguration =
            this.eventForm.controls.systemEventActionId.value === SystemEventAction.EvaluateUwwRequirementRules
            || this.eventForm.controls.systemEventActionId.value === SystemEventAction.EvaluateUwwTaskRules;

        if (actionRequiresConditionConfiguration && !hasActionConditionValue) {
            this.selectedEvent.configuration = "";
        }

        if (this.eventForm.controls.systemEventActionId.value === SystemEventAction.StatusChange && hasActionConditionValue) {
            this.selectedEvent.configuration = JSON.stringify({ 'statusId': actionConditionValue });
        }

        if ((this.eventForm.controls.systemEventActionId.value === SystemEventAction.BillingAdd || this.eventForm.controls.systemEventActionId.value === SystemEventAction.BillingRemove) && hasActionConditionValue) {
            this.selectedEvent.configuration = JSON.stringify({
                'billingEventIds': Array.isArray(actionConditionValue) ? actionConditionValue.join(',') : `${actionConditionValue}`
            });
        }

        if (this.eventForm.controls.systemEventActionId.value === SystemEventAction.EvaluateUwwRequirementRules && hasActionConditionValue) {
            this.selectedEvent.configuration = JSON.stringify({
                'requirementIds': Array.isArray(actionConditionValue) ? actionConditionValue.join(',') : `${actionConditionValue}`
            });
        }
        
        if (this.eventForm.controls.systemEventActionId.value === SystemEventAction.EvaluateUwwTaskRules && hasActionConditionValue) {
            this.selectedEvent.configuration = JSON.stringify({
                'taskIds': Array.isArray(actionConditionValue) ? actionConditionValue.join(',') : `${actionConditionValue}`
            });
        }

        if (this.eventForm.controls.systemEventActionId.value === SystemEventAction.Fax) {
            if (!this.selectedEvent.configuration || this.selectedEvent.configuration === null) {
                this.notificationService.showNotification({
                    severity: NotificationSeverity.Error,
                    message: 'Please add action properties...',
                    log: false
                });

                return false;
            }
        }

        if (this.eventForm.controls.systemEventActionId.value === SystemEventAction.MarkCaseDone) {
            this.selectedEvent.configuration = JSON.stringify({ 'regenerateDocuments': this.eventForm.get('regenerateDocuments').value });
        }

        const accountEvent: AccountEvent = this.eventForm.value;
        accountEvent.accountId = this.accountInfo.id;
        accountEvent.accountMessage = this.selectedEvent.accountMessage;
        accountEvent.configuration = this.selectedEvent.configuration;

        if (accountEvent.systemEventActionId === SystemEventAction.SendSMS) {
            accountEvent.accountMessage.type = SystemEventActionType.SMS;
        }

        this.setConditions(accountEvent);

        if (accountEvent.systemEventId === this.timerTriggerEnum) {
            this.warnAboutTimerEventImpact(accountEvent);
            return;
        }

        this.persistAccountEvent(accountEvent);
    }

    /**
     * Timer events re-evaluate every matching case every hour, so always show the configurer how many production
     * cases the search window covers before they commit to it.
     */
    private warnAboutTimerEventImpact(accountEvent: AccountEvent) {
        const conditions = accountEvent.conditions ? JSON.parse(accountEvent.conditions) : {};

        this.accountEventsService.getTimerEventCaseCount(this.accountInfo.id, {
            tenantId: this.accountInfo.clientId,
            searchDaysBack: conditions.searchDaysBack,
            statuses: conditions.caseStatusV2 ? conditions.caseStatusV2.split(',') : [],
            minSearchDaysBack: conditions.minSearchDaysBack,
            dateFieldMapkey: conditions.dateFieldMapkey || null
        }).subscribe({
            next: result => {
                if (!result) {
                    this.persistAccountEvent(accountEvent);
                    return;
                }

                this.confirmationService.open({
                    message: this.buildTimerEventImpactMessage(result),
                    showCancel: true,
                    onOk: () => this.persistAccountEvent(accountEvent)
                });
            },
            error: () => {
                // The preview is advisory only, so a failure to reach production must not block saving.
                this.persistAccountEvent(accountEvent);
            }
        });
    }

    private buildTimerEventImpactMessage(result: TimerEventCaseCountResponse): string {
        const cases = result.caseCount === 1 ? '1 production case' : `${result.caseCount} production cases`;
        const summary = `This event currently matches ${cases} over the last ${result.searchDaysBack} days.`;

        if (result.caseCount === 0) {
            return `${summary} Nothing will be evaluated until a case matches these conditions. Save anyway?`;
        }

        if (result.caseCount <= result.maxCasesPerRun) {
            return `${summary} They will be re-evaluated every hour. Save anyway?`;
        }

        const runs = Math.ceil(result.caseCount / result.maxCasesPerRun);

        return `${summary} Only ${result.maxCasesPerRun} cases are evaluated per hourly run, `
            + `so it will take about ${runs} hours to work through them all. Save anyway?`;
    }

    private persistAccountEvent(accountEvent: AccountEvent) {
        if (this.selectedEvent.id) {
            if (this.eventForm.valid) {
                this.saveEditAccount(accountEvent);
                this.formSubmitted = false;
            }
        } else {
            if (this.eventForm.valid) {
                this.saveAddAccount(accountEvent);
                this.formSubmitted = false;
            }
        }

        this.appService.getAllDynamicConfigData(this.accountInfo.id).then();
    }

    setConditions(accountEvent: AccountEvent) {
        const eventType = _.find(this.eventTypes, { 'id': accountEvent.systemEventId });

        if (eventType.params) {
            const params = JSON.parse(eventType.params);
            const conditions: any = {};
            params.forEach(param => {
                if (param.lookupEnum && param.paramType !== 'multi-lookup') {
                    conditions[param.lookupEnum] = accountEvent.conditionId;
                }
                else if (param.paramType === 'rawJson') {
                    const selectedParam = _.find(params, { 'id': accountEvent.conditionId });
                    // Because there is a feature flag on Order selection for the process interview event,
                    // when the feature flag is disabled and an account was previously configured to use order while the feature flag was
                    // enabled. Now the feature flag is disabled there is no order to be selected.
                    if (selectedParam !== undefined) {
                        accountEvent.conditions = selectedParam.code;
                    }

                } else if (param.paramType === 'toggle') {
                    conditions[param.name] = accountEvent[param.name];
                } else if (param.paramType === 'multi-lookup') {
                    if (param.name) {
                        const val = this.eventForm.get(param.name)?.value;
                        conditions[param.name] = Array.isArray(val) ? val.join(',') : `${val ?? ''}`;
                    } else {
                        // When paramType is multi-lookup the conditionId should be an array.
                        if (Array.isArray(accountEvent.conditionId)) {
                            conditions['caseStatusV2'] = accountEvent.conditionId.join(',');
                        } else {
                            conditions['caseStatusV2'] = `${accountEvent.conditionId}`;
                        }
                    }
                } else if (param.paramType === 'mapKeys' || param.paramType === 'input' || param.paramType === 'dateField') {
                    conditions[param.name] = accountEvent[param.name];                
                } else if (param.paramType === 'tags') {
                    conditions[param.name] = this.selectedTags.join(',');
                }
            });

            if (!_.isEmpty(conditions)) {
                accountEvent.conditions = JSON.stringify(conditions);
            }
        }
    }

    saveEditAccount(accountEvent: AccountEvent) {
        this.accountEventsService.editAccountEvent(accountEvent).subscribe(_res => {
            this.appService.showMsg('success', 'Edited successfully ...');
            this.getAccountEvents();
            this.selectedEvent = null;
        });
    }

    saveAddAccount(accountEvent: AccountEvent) {
        accountEvent.isActive = true;
        this.accountEventsService.addAccountEvent(accountEvent).subscribe(_res => {
            this.appService.showMsg('success', 'Added successfully ...');
            this.getAccountEvents();
            this.selectedEvent = null;
        });
    }

    deleteAccountEvent(accountEvent: AccountEvent) {
        this.confirmationService.open({
            message: `Are you sure that you want to delete this account event?`,
            showCancel: true,
            onOk: () => {
                this.accountEventsService.deleteAccountEvent(accountEvent).subscribe(
                    _data => {
                        this.appService.showMsg('success', 'Deleted successfully ...');
                        this.getAccountEvents();
                        this.selectedEvent = null;
                    }
                );
            }
        });
    }

    filterEventActions() {
        const actionFilters = this.systemEventActionFilters.filter(x => x.systemEventId == this.eventForm.controls['systemEventId'].value);
        this.filteredActions = this.actions;

        if (actionFilters && actionFilters.length > 0) {
            const filterIds = actionFilters.map(a => a.systemEventActionId);
            this.filteredActions = this.actions.filter((action) => filterIds.includes(action.id));
            const exists = this.filteredActions.filter(x => x.id == this.selectedEvent.systemEventActionId);
            if (!(exists && exists.length > 0)) {
                this.selectedEvent.systemEventActionId = null;
                this.eventForm.patchValue(this.selectedEvent);
            }
        }
    }
}